import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Grid, Header, Button, Icon, Modal, Dropdown, Message } from 'semantic-ui-react';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { CharactersListEnh } from '../lists/';
import { CharacterFormEnh } from '../forms';
import { useCharacterManagement, useSharedData } from '../../hooks/core/useZoneHooks';
import '../../styles/voiceActorWorksheet.css';

/**
 * CharacterManagementEnh component
 * Enhanced version of CharacterManagement adapted from BookWorksheet
 * Manages characters for a selected portfolio
 * EXACT SAME UI and functionality as BookWorksheet, just adapted for VoiceActor entities
 * @returns {JSX.Element} CharacterManagementEnh component
 */
const CharacterManagementEnh = () => {
  // 🚀 PERFORMANCE OPTIMIZATION: Add render tracking
  const renderCount = useRef(0);
  renderCount.current++;

  if (process.env.NODE_ENV === 'development' && window.DEBUG_RENDERS) {
    console.log(`[RENDER] VoiceActor CharacterManagement render #${renderCount.current}`);
  }

  // 🚀 NEW: Use zone-based hooks for optimal performance
  const characterManagement = useCharacterManagement();
  const sharedData = useSharedData();

  // Extract data from hooks
  const {
    selectedCharacter,
    actions: characterActions
  } = characterManagement;

  const {
    actors,
    portfolios,
    selections,
    actions: sharedActions
  } = sharedData;

  // Map to interface for compatibility with safety checks
  const actorsArray = Array.isArray(actors) ? actors : [];
  const portfoliosArray = useMemo(() => Array.isArray(portfolios) ? portfolios : [], [portfolios]);
  const selectionsObj = selections || {};

  const selectedPortfolio = portfoliosArray.find(portfolio => portfolio.uuid === selectionsObj.portfolioId) || null;
  const actorId = selectionsObj.actorId;
  const portfolioId = selectionsObj.portfolioId;
  const characterId = selectionsObj.characterId;

  // Memoized actions to prevent dependency issues
  const setActorSelection = useMemo(() => sharedActions.setActorSelection, [sharedActions.setActorSelection]);
  const setPortfolioSelection = useMemo(() => sharedActions.setPortfolioSelection, [sharedActions.setPortfolioSelection]);
  const setCharacterSelection = useMemo(() => characterActions.selectCharacter, [characterActions.selectCharacter]);
  const fetchActors = useMemo(() => sharedActions.fetchActors, [sharedActions.fetchActors]);
  const fetchPortfolios = useMemo(() => sharedActions.fetchPortfolios, [sharedActions.fetchPortfolios]);
  const fetchCharacters = useMemo(() => characterActions.fetchCharacters, [characterActions.fetchCharacters]);

  // Memoized helper functions
  const clearCharacterSelection = useCallback(() => characterActions.selectCharacter(null), [characterActions]);
  const fetchCharacter = useMemo(() => characterActions.fetchCharacter || (() => Promise.resolve()), [characterActions.fetchCharacter]);
  const fetchActor = useCallback(() => Promise.resolve(), []); // Not needed in new architecture
  const fetchPortfolio = useCallback(() => Promise.resolve(), []); // Not needed in new architecture
  const getPortfoliosByActor = useCallback((actorId) => portfoliosArray.filter(portfolio => portfolio.actorId === actorId), [portfoliosArray]);

  // Derived data computed from hook data
  const portfoliosForActor = actorId ? getPortfoliosByActor(actorId) : [];
  const actorOptions = actorsArray.map(actor => ({
    key: actor.uuid,
    text: `${actor.firstName} ${actor.lastName}` || 'Unnamed Actor',
    value: actor.uuid
  }));

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Update editing state when character ID changes
  useEffect(() => {
    setIsEditing(!!characterId);
  }, [characterId]);

  // 🚀 NEW: Initialize data fetching on mount
  useEffect(() => {
    // Fetch initial data when component mounts
    fetchActors();
  }, [fetchActors]);

  // Auto-fetch portfolios when actorId changes
  useEffect(() => {
    if (actorId) {
      fetchPortfolios(actorId);
    }
  }, [actorId, fetchPortfolios]);

  // Auto-fetch characters when portfolioId changes
  useEffect(() => {
    console.log('[VA_CHARACTER_MGMT] useEffect triggered - portfolioId:', portfolioId, 'fetchCharacters:', typeof fetchCharacters);
    if (portfolioId) {
      console.log('[VA_CHARACTER_MGMT] Calling fetchCharacters for portfolioId:', portfolioId);
      fetchCharacters(portfolioId);
    }
  }, [portfolioId, fetchCharacters]);

  // Conditional logging for performance
  if (process.env.NODE_ENV === 'development' && window.DEBUG_CHARACTER_MANAGEMENT) {
    console.log('Actor ID:', actorId);
    console.log('Portfolios for Actor:', portfoliosForActor);
    console.log('Selected Portfolio:', selectedPortfolio);
  }

  // Create dropdown options from all available portfolios
  let portfolioOptions = portfoliosForActor.map(portfolio => ({
    key: portfolio.uuid,
    text: portfolio.name || portfolio.title || 'Unnamed Portfolio',
    value: portfolio.uuid
  }));

  // If we have a selected portfolio but it's not in the options, add it
  if (selectedPortfolio && !portfolioOptions.some(option => option.value === selectedPortfolio.uuid)) {
    portfolioOptions.unshift({
      key: selectedPortfolio.uuid,
      text: selectedPortfolio.name || selectedPortfolio.title || 'Selected Portfolio',
      value: selectedPortfolio.uuid
    });
  }

  // Conditional logging for performance
  if (process.env.NODE_ENV === 'development' && window.DEBUG_CHARACTER_MANAGEMENT) {
    console.log('Portfolio Options:', portfolioOptions);
  }

  // Handle new character button click
  const handleNewCharacterClick = useCallback(() => {
    clearCharacterSelection();
  }, [clearCharacterSelection]);

  // Handle delete character
  const handleDeleteCharacter = useCallback((character) => {
    setCharacterToDelete(character);
    setShowDeleteModal(true);
  }, []);

  // Handle confirm delete character
  const confirmDeleteCharacter = useCallback(async () => {
    if (!characterToDelete?.uuid) return;

    try {
      // Call the unified delete thunk with enhanced options
      await characterActions.deleteCharacter({
        characterId: characterToDelete.uuid,
        portfolioId,
        options: {
          cleanup: 'cleanup',    // Enable S3 cleanup
          cascade: 'cascade',    // Enable cascade deletion
          dryRun: false,         // Actually perform deletion
          force: false           // Use safety checks
        }
      });

      // Close the modal
      setShowDeleteModal(false);
      setCharacterToDelete(null);

      // If the deleted character was selected, clear the selection
      if (selectedCharacter && selectedCharacter.uuid === characterToDelete.uuid) {
        clearCharacterSelection();
      }

      // Refresh the characters list
      if (portfolioId) {
        fetchCharacters(portfolioId);
      }

      setSuccessMessage('Character deleted successfully with cascade cleanup');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting character via unified system:', error);
      setErrorMessage('Failed to delete character');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  }, [characterToDelete, characterActions, portfolioId, selectedCharacter, clearCharacterSelection, fetchCharacters]);

  // Handle actor selection
  const handleActorSelect = useCallback((value) => {
    setActorSelection(value);
    clearCharacterSelection();

    // Fetch the actor data
    fetchActor(value);

    // Fetch portfolios for this actor
    fetchPortfolios(value).then(() => {
      // After fetching portfolios, get the updated list of portfolios for this Actor
      const updatedPortfolios = getPortfoliosByActor(value);
      console.log('Updated portfolios after fetch:', updatedPortfolios);

      // If there are portfolios, select the first one with a profile, or the first one if none have profiles
      if (updatedPortfolios && updatedPortfolios.length > 0) {
        // Find a portfolio with a profile (not profileMissing)
        const portfolioWithProfile = updatedPortfolios.find(portfolio => !portfolio.profileMissing);

        if (portfolioWithProfile) {
          console.log('Selecting portfolio with profile:', portfolioWithProfile);
          setPortfolioSelection(portfolioWithProfile.uuid);
        } else {
          console.log('No portfolios with profiles found, selecting first portfolio:', updatedPortfolios[0]);
          setPortfolioSelection(updatedPortfolios[0].uuid);
        }
      }
    });
  }, [setActorSelection, clearCharacterSelection, fetchActor, fetchPortfolios, getPortfoliosByActor, setPortfolioSelection]);

  // Handle portfolio selection
  const handlePortfolioSelect = useCallback((value) => {
    setPortfolioSelection(value);
    clearCharacterSelection();

    // Fetch the portfolio data
    fetchPortfolio(value);

    // Fetch characters for this portfolio
    fetchCharacters(value);
  }, [setPortfolioSelection, clearCharacterSelection, fetchPortfolio, fetchCharacters]);

  // Handle character selection
  const handleCharacterSelect = useCallback((character) => {
    if (character && character.uuid) {
      // Set the selection first
      setCharacterSelection(character.uuid);
      // Then fetch the character data using the zone-based hook method
      fetchCharacter(character.uuid, portfolioId)
        .catch(error => {
          console.error('Error fetching character:', error);
        });
      setIsEditing(true);
    }
  }, [setCharacterSelection, fetchCharacter, portfolioId]);

  // Handle save
  const handleSaveCharacter = useCallback((updatedCharacter) => {
    // Refresh data after save
    if (updatedCharacter && updatedCharacter.uuid) {
      // Set the selection first
      setCharacterSelection(updatedCharacter.uuid);
      // Then fetch the character data using the zone-based hook method
      fetchCharacter(updatedCharacter.uuid, portfolioId);
      setIsEditing(true);

      // Refresh the characters list
      if (portfolioId) {
        fetchCharacters(portfolioId);
      }

      setSuccessMessage('Character saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [setCharacterSelection, fetchCharacter, portfolioId, fetchCharacters]);

  return (
    <ManagementHeader
      title={<div><span>Character Management:  </span><span className="va-subTitle">{selectedPortfolio?.name || ''}</span></div>}
      error={errorMessage}
      isLoading={false}
      width="1024px"
      isEditing={isEditing}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <div>
              <div style={{ marginBottom: '1em', display: 'none' }}>
                <div style={{
                  marginTop: '0.6em',
                  position: 'relative'
                }}>
                  <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Voice Actor</label>
                  <Dropdown
                    selection
                    options={actorOptions}
                    value={actorId}
                    onChange={(e, { value }) => handleActorSelect(value)}
                    placeholder="Select Voice Actor"
                    fluid
                    style={{ zIndex: 1000 }}
                    disabled={actorOptions.length === 0}
                  />
                </div>
              </div>

              {actorId && 1 === -0 && (
                <div style={{ marginBottom: '1em' }}>
                  <div style={{
                    marginTop: '0.6em',
                    position: 'relative'
                  }}>
                    <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Portfolio</label>
                    <Dropdown
                      selection
                      options={portfolioOptions}
                      value={portfolioId}
                      onChange={(e, { value }) => handlePortfolioSelect(value)}
                      placeholder="Select Portfolio"
                      fluid
                      style={{ zIndex: 999 }}
                      disabled={portfolioOptions.length === 0}
                    />
                    {portfolioOptions.length === 0 && actorId && (
                      <Message info size="tiny" style={{ marginTop: '0.5em' }}>
                        <p>No portfolios found for this voice actor. Please create a portfolio first.</p>
                      </Message>
                    )}
                  </div>
                </div>
              )}

              {selectedPortfolio && (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <Header as="h3" style={{ margin: 0 }}>Characters</Header>
                    <Button
                      primary
                      size="small"
                      icon
                      labelPosition="left"
                      onClick={handleNewCharacterClick}
                      style={{ marginRight: '10px' }}
                    >
                      <Icon name="add" />
                      New Character
                    </Button>
                  </div>

                  <CharactersListEnh
                    onCharacterSelect={handleCharacterSelect}
                    onDeleteCharacter={handleDeleteCharacter}
                  />
                </div>
              )}
            </div>
          </Grid.Column>

          <Grid.Column width={10}>
            {selectedPortfolio && (
              <>
                {successMessage && (
                  <Message positive>
                    <Message.Header>Success</Message.Header>
                    <p>{successMessage}</p>
                  </Message>
                )}
                <CharacterFormEnh onSave={handleSaveCharacter} />
              </>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} size="small">
        <Modal.Header>Delete Character</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to delete this character? This action cannot be undone.</p>
          {characterToDelete && (
            <Message warning>
              <Message.Header>You are about to delete:</Message.Header>
              <p><strong>{characterToDelete.name}</strong></p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button negative onClick={confirmDeleteCharacter}>
            Delete
          </Button>
          <Button onClick={() => {
            setShowDeleteModal(false);
            setCharacterToDelete(null);
          }}>
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>
    </ManagementHeader>
  );
};

// 🚀 PERFORMANCE OPTIMIZATION: Memoized to prevent unnecessary re-renders
export default React.memo(CharacterManagementEnh);
